"use client";

import Link from "next/link";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import PrimaryButton from "@/components/ui/primary-button";
import { cn, withUrl } from "@/lib/utils";
import { createInquiry, InquiryFormErrors } from "./inquiry-form-actions";
import { useActionState } from "react";
import { toast } from "sonner";
import { createRef, useEffect, useState } from "react";
import InquiryThankYou from "./inquiry-thank-you";
import LINKS from "@/lib/links";
import ReCAPTCHA from "react-google-recaptcha";

export interface InquiryFormProps {
  className?: string;
}

const initialState: {
  status: string;
  errors?: InquiryFormErrors;
  message?: string;
} = { status: "" };

const InquiryForm: React.FC<InquiryFormProps> = ({ className }) => {
  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const handleRecaptchaChange = setRecaptchaToken;
  const createWithToken = createInquiry.bind(null, recaptchaToken);

  const [state, formAction, isPending] = useActionState(
    createWithToken,
    initialState
  );

  useEffect(() => {
    if (state?.message) {
      toast.error(state?.message);
    }
  }, [state]);

  return (
    <>
      {state.status === "success" ? (
        <div
          className={cn(
            "flex w-full flex-col gap-4 rounded-3xl bg-white px-4 py-6 md:p-8 lg:max-w-[680px]",
            className
          )}
        >
          <InquiryThankYou className="col-span-2 lg:col-span-1" />
        </div>
      ) : (
        <div
          className={cn(
            "flex w-full flex-col gap-4 rounded-3xl bg-white px-4 py-6 md:p-8 lg:max-w-[680px]",
            className
          )}
        >
          <p className="font-montserrat text-heading-4 text-secondary-500 md:text-heading-3">
            Inquiry Form
          </p>
          <form
            id="inquiry-home-form"
            action={formAction}
          >
            <p className="font-roboto text-body-1-300 text-neutral-400">
              All required fields are marked with an asterisk.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Input
                className="col-span-2 md:col-span-1"
                color="white"
                id="inquiry-first-name"
                label="First Name"
                autoComplete="given-name"
                name="firstName"
                required
                error={
                  state.errors?.firstName && state.errors.firstName.length > 0
                }
                errorMessage={
                  state.errors?.firstName && state.errors.firstName.length > 0
                    ? state.errors.firstName.join(", ")
                    : "First name is required"
                }
              />
              <Input
                className="col-span-2 md:col-span-1"
                color="white"
                id="inquiry-last-name"
                label="Last Name"
                autoComplete="family-name"
                name="lastName"
                required
                error={
                  state.errors?.lastName && state.errors.lastName.length > 0
                }
                errorMessage={
                  state.errors?.lastName && state.errors.lastName.length > 0
                    ? state.errors.lastName.join(", ")
                    : "Last name is required"
                }
              />
              <Input
                className="col-span-2"
                color="white"
                id="inquiry-email"
                label="Email Address"
                type="email"
                name="email"
                autoComplete="email"
                required
                error={state.errors?.email && state.errors.email.length > 0}
                errorMessage={
                  state.errors?.email && state.errors.email.length > 0
                    ? state.errors.email.join(", ")
                    : "Email is required"
                }
              />
              <Input
                className="col-span-2"
                name="phoneNumber"
                color="white"
                id="inquiry-phone-number"
                label="Phone Number"
                required
                error={
                  state.errors?.phoneNumber &&
                  state.errors.phoneNumber.length > 0
                }
                errorMessage={
                  state.errors?.phoneNumber &&
                  state.errors.phoneNumber.length > 0
                    ? state.errors.phoneNumber.join(", ")
                    : "Phone number is required"
                }
              />
              <Textarea
                className="col-span-2"
                // Should probably named "message" once it has its own salesforce mapping
                // This is named "specificNeeds" because it shares mapping with the standalone inquiry form
                name="specificNeeds"
                id="inquiry-specific-needs"
                label="Message (Optional)"
                rows={3}
                error={
                  state.errors?.specificNeeds &&
                  state.errors.specificNeeds.length > 0
                }
                errorMessage={
                  state.errors?.specificNeeds &&
                  state.errors.specificNeeds.length > 0
                    ? state.errors.specificNeeds.join(", ")
                    : undefined
                }
              />
              <div className="col-span-2 flex flex-row gap-1 space-x-1">
                <label
                  htmlFor="inquiry-consent"
                  className="font-roboto text-body-1-300 text-neutral-400"
                >
                  By Submitting, you agree to Care Indeed&apos;s&nbsp;
                  <span className="underline">
                    <Link
                      href={withUrl(LINKS.termsOfUse.href)}
                      prefetch={!LINKS.termsOfUse.doNotPrefetch}
                    >
                      {LINKS.termsOfUse.title}
                    </Link>
                  </span>
                  &nbsp;and&nbsp;
                  <span className="underline">
                    <Link
                      href={withUrl(LINKS.privacyPolicy.href)}
                      prefetch={!LINKS.privacyPolicy.doNotPrefetch}
                    >
                      {LINKS.privacyPolicy.title}.
                    </Link>
                  </span>
                </label>
              </div>

              <div className="col-span-2">
                {recaptchaKey && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaKey}
                    onChange={handleRecaptchaChange}
                  />
                )}
              </div>

              <div className="col-span-2">
                <PrimaryButton
                  className=""
                  color="teal"
                  size="md"
                  disabled={isPending}
                >
                  Submit Inquiry
                </PrimaryButton>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default InquiryForm;
